import { Dropdown, Col, Row, Space, Typography, Modal, message } from "antd";
import { useState } from "react";
import QuestionnairesFormDialog from "../components/questionnaires/QuestionnairesFormDialog";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import {
  getQuestionnairesExport,
  newQuestionnaireVersion,
  publishQuestionnaire,
} from "../repository/data.repository";
import Loader from "../controls/Loader";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import QuestionnairesView from "../components/questionnaires/QuestionnairesView";
import { Questionnaire } from "../repository/data.default";
import { getProblemMessage } from "../utils/http";

// The server explains a refused action (e.g. what a draft still lacks before it can be published).
const problemDetail = (err: any, fallback: string): string => err?.response?.data?.detail || fallback;

function QuestionnairesPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [questionnaireInputMode, setQuestionnaireInputMode] = useState<
    "add" | "update" | "delete"
  >("add");
  const [questionnaireInputOpen, setQuestionnaireInputOpen] = useState(false);
  const [questionnaireInputReadOnly, setQuestionnaireInputReadOnly] = useState(false);
  const [questionnaireInputValues, setQuestionnaireInputValues] =
    useState<Questionnaire>({} as any);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const [busy, setBusy] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function refresh(): void {
    setTriggerView(Guid.create().toString());
  }

  function onAction(key: string, record: Questionnaire): void {
    switch (key) {
      case "view":
        return onQuestionnaireInputOpen("update", record, true);
      case "newVersion":
        return onNewVersion(record);
      case "publish":
        return onPublish(record);
      default:
        return onQuestionnaireInputOpen(key as any, record);
    }
  }

  function onNewVersion(record: Questionnaire): void {
    modal.confirm({
      title: `Start version ${record.version + 1} of "${record.name}"?`,
      content:
        "A draft copy of the published version is created for you to edit. The published version stays in use, unchanged, until you publish the draft.",
      okText: "Create draft",
      centered: true,
      onOk: () => {
        if (busy) return;
        setBusy(true);
        return newQuestionnaireVersion(record.uid)
          .then((draft) => {
            refresh();
            message.success(`Version ${draft.version} created as a draft`);
            onQuestionnaireInputOpen("update", draft);
          })
          .catch((err) => message.error(problemDetail(err, "Couldn't create the new version — please retry.")))
          .finally(() => setBusy(false));
      },
    });
  }

  function onPublish(record: Questionnaire): void {
    modal.confirm({
      title: `Publish version ${record.version} of "${record.name}"?`,
      content:
        record.version > 1
          ? `Version ${record.version - 1} is retired, and roadmap steps, bundles and reports move onto this version. Answers already given stay on the version they were given on. A published version can no longer be edited.`
          : "It becomes available to roadmap steps and bundles. A published version can no longer be edited; changes are made in a new version.",
      okText: "Publish",
      centered: true,
      onOk: () => {
        if (busy) return;
        setBusy(true);
        return publishQuestionnaire(record.uid)
          .then(() => {
            refresh();
            message.success(`Version ${record.version} published`);
          })
          .catch((err) => message.error(problemDetail(err, "Couldn't publish — please retry.")))
          .finally(() => setBusy(false));
      },
    });
  }

  function onQuestionnaireInputOpen(
    mode: "add" | "update" | "delete",
    values?: Questionnaire,
    readOnly = false,
  ): void {
    setQuestionnaireInputMode(mode);
    setQuestionnaireInputReadOnly(readOnly);
    setQuestionnaireInputOpen(true);
    setQuestionnaireInputValues(values || ({} as any));
  }

  function onQuestionnaireInputFinish(
    mode?: "update",
    values: Questionnaire = {} as any,
  ): void {
    setQuestionnaireInputOpen(mode === "update" ? true : false);
    if (mode === "update") setQuestionnaireInputMode(mode);
    setQuestionnaireInputValues(values);
    setTriggerView(Guid.create().toString());
  }

  function onQuestionnaireInputClose(): void {
    setQuestionnaireInputOpen(false);
    setQuestionnaireInputValues({} as any);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading questionnaires export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getQuestionnairesExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Questionnaires
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("Questionnaires", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            className={permissions.can("Questionnaires", "create") ? undefined : "disabled"}
            onClick={() => permissions.can("Questionnaires", "create") && onQuestionnaireInputOpen("add")}
          >
            Create
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <QuestionnairesView
          trigger={triggerView}
          onAction={(key: string, record: any) => onAction(key, record)}
          onSearch={onSearch}
        />
      </Col>
      <QuestionnairesFormDialog
        mode={questionnaireInputMode}
        open={questionnaireInputOpen}
        initialValues={questionnaireInputValues}
        readOnly={questionnaireInputReadOnly}
        onFinish={(mode, values) => onQuestionnaireInputFinish(mode, values)}
        onCancel={onQuestionnaireInputClose}
      />
      {contextHolder}
    </Col>
  );
}

export default QuestionnairesPage;
